import { type EnterpriseCustomWorkflow } from "@mg/schemas/src/commons/enterprise";
import {
  Lightbulb,
  Megaphone,
  PaintBrush,
  PlugsConnected,
  UsersThree,
  Wrench,
} from "@phosphor-icons/react";
import { Heading, Text } from "@radix-ui/themes";
import cx from "classnames";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { WORKFLOW_CARDS } from "../mockData";

interface SkillCardsProps {
  onCardClick: (workflowType: string) => void;
  enabledSkillIds?: string[];
  customWorkflows?: EnterpriseCustomWorkflow[];
}

// Keyed by WORKFLOW_CARDS `id`. Monochrome outline icons to match the flat
// card aesthetic; tinting lives in the surrounding shell, not the glyph.
const WORKFLOW_ICONS: Record<string, React.ReactNode> = {
  marketing_review: (
    <Megaphone size={22} className="text-puntt-neutral-gray-12" />
  ),
  artwork_review: (
    <PaintBrush size={22} className="text-puntt-neutral-gray-12" />
  ),
  claims_intelligence: (
    <Lightbulb size={22} className="text-puntt-neutral-gray-12" />
  ),
  shared_projects: (
    <UsersThree size={22} className="text-puntt-neutral-gray-12" />
  ),
  connectors: (
    <PlugsConnected size={22} className="text-puntt-neutral-gray-12" />
  ),
};

// Uniform thin-bordered icon box (flat at rest) that picks up an accent ring
// on card hover for every card.
const WORKFLOW_ICON_SHELL = "border border-puntt-neutral-gray-6 bg-base-white";

const WORKFLOW_ICON_BG: Record<string, string> = {
  marketing_review: WORKFLOW_ICON_SHELL,
  artwork_review: WORKFLOW_ICON_SHELL,
  claims_intelligence: WORKFLOW_ICON_SHELL,
  shared_projects: WORKFLOW_ICON_SHELL,
  connectors: WORKFLOW_ICON_SHELL,
};

const SKILL_CARD_INTERACTION =
  "shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-puntt-accent-7 hover:shadow-lg";

type SkillCardLayout = "single" | "pair" | "grid";

interface RenderableCard {
  key: string;
  testid: string;
  clickId: string;
  icon: ReactNode;
  iconClassName: string;
  title: string;
  description: string;
}

function resolveSkillCardLayout(totalCards: number): SkillCardLayout {
  if (totalCards <= 1) return "single";
  if (totalCards === 2) return "pair";
  return "grid";
}

/**
 * Every card sits in a single row spanning the full composer width — the grid
 * never wraps, so all enabled skills stay on one line.
 */
function resolveGridRowSizes(totalCards: number): number[] {
  return [totalCards];
}

function skillCardShellClass(layout: SkillCardLayout) {
  return cx(
    "group flex rounded-2xl border border-puntt-neutral-gray-6 bg-base-white text-left",
    SKILL_CARD_INTERACTION,
    layout === "single" &&
      "w-full flex-row items-center gap-4 p-5 hover:translate-y-0",
    layout === "pair" &&
      "min-w-0 flex-1 basis-0 flex-row items-center gap-3 p-4 hover:translate-y-0",
    // flex-1 basis-0 lets each card stretch to fill its row's width.
    layout === "grid" && "min-w-0 flex-1 basis-0 flex-col gap-2 p-3",
  );
}

function SkillCardBody({
  layout,
  icon,
  iconClassName,
  title,
  description,
}: {
  layout: SkillCardLayout;
  icon: ReactNode;
  iconClassName: string;
  title: string;
  description: string;
}) {
  const isHorizontal = layout === "single" || layout === "pair";

  const iconShell = (
    <div
      className={cx(
        "flex shrink-0 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-105 group-hover:ring-2 group-hover:ring-puntt-accent-5/50",
        layout === "single" ? "size-12" : "size-10",
        iconClassName,
      )}
    >
      {icon}
    </div>
  );

  const copy = (
    <div className={cx("min-w-0", isHorizontal && "flex-1")}>
      <Heading
        as="h3"
        size="2"
        weight="bold"
        className="text-puntt-neutral-gray-12"
      >
        {title}
      </Heading>
      <Text
        as="p"
        size="1"
        className="mt-1 leading-relaxed text-puntt-neutral-gray-11"
      >
        {description}
      </Text>
    </div>
  );

  return (
    <>
      {iconShell}
      {copy}
    </>
  );
}

/** @deprecated Use SkillCards instead */
export function WorkflowCards(props: SkillCardsProps) {
  return <SkillCards {...props} />;
}

export function SkillCards({
  onCardClick,
  enabledSkillIds,
  customWorkflows,
}: SkillCardsProps) {
  const { t } = useTranslation("ui");
  const filteredCards = enabledSkillIds
    ? WORKFLOW_CARDS.filter((card) => enabledSkillIds.includes(card.skillId))
    : WORKFLOW_CARDS;

  const enabledCustomWorkflows = (customWorkflows ?? []).filter(
    (wf) => wf.enabled,
  );

  const totalCards = filteredCards.length + enabledCustomWorkflows.length;
  const layout = resolveSkillCardLayout(totalCards);
  const cardClassName = skillCardShellClass(layout);

  // Flatten default + custom workflows into one ordered list rendered as a
  // single row.
  const orderedCards: RenderableCard[] = [
    ...filteredCards.map((card) => ({
      key: card.id,
      testid: `ask-puntt-skill-card-${card.id}`,
      clickId: card.id,
      icon: WORKFLOW_ICONS[card.id],
      iconClassName: WORKFLOW_ICON_BG[card.id] ?? "bg-puntt-neutral-gray-2",
      title: t(`workflowCards.${card.id}.title`),
      description: t(`workflowCards.${card.id}.description`),
    })),
    ...enabledCustomWorkflows.map((wf) => ({
      key: wf.workflowId,
      testid: `ask-puntt-skill-card-custom_${wf.workflowId}`,
      clickId: `custom_${wf.workflowId}`,
      icon: <Wrench size={22} className="text-puntt-neutral-gray-12" />,
      iconClassName: WORKFLOW_ICON_SHELL,
      title: wf.name,
      description: wf.trigger,
    })),
  ];

  const renderCard = (card: RenderableCard) => {
    const body = (
      <SkillCardBody
        layout={layout}
        icon={card.icon}
        iconClassName={card.iconClassName}
        title={card.title}
        description={card.description}
      />
    );

    // Every card renders wrapper + button so the row's flex items are all the
    // same box. They must be: with `flex-basis: 0` and border-box, padding +
    // border are added ON TOP of each item's equal share of the free space, so
    // a padded card as the flex item comes out `p-3` + border = 26px wider than
    // a padding-less wrapper.
    const wrapperSizing =
      layout === "single" ? "w-full" : "min-w-0 flex-1 basis-0";
    return (
      <div key={card.key} className={cx("relative", wrapperSizing)}>
        <button
          data-testid={card.testid}
          onClick={() => onCardClick(card.clickId)}
          className={cx(
            cardClassName,
            // h-full, not just w-full: the WRAPPER is the flex item and stretches
            // to the row, but the visible border lives on this button — without
            // it the button is only as tall as its own text, so cards with a
            // two-line description end short of their three-line neighbours.
            "h-full w-full",
          )}
        >
          {body}
        </button>
      </div>
    );
  };

  // single / pair: a single flex row of horizontal cards.
  if (layout !== "grid") {
    return (
      <div
        className="flex w-full gap-3"
        data-testid="ask-puntt-skill-cards-grid"
        data-layout={layout}
      >
        {orderedCards.map(renderCard)}
      </div>
    );
  }

  // grid: a single row holding every card, each stretching to fill the width.
  const rowSizes = resolveGridRowSizes(totalCards);
  let cursor = 0;
  const rows = rowSizes.map((size) => {
    const slice = orderedCards.slice(cursor, cursor + size);
    cursor += size;
    return slice;
  });

  return (
    <div
      className="flex w-full flex-col gap-3"
      data-testid="ask-puntt-skill-cards-grid"
      data-layout={layout}
      data-row-sizes={rowSizes.join(",")}
    >
      {rows.map((rowCards, rowIndex) => (
        <div
          key={rowCards.map((card) => card.key).join("|")}
          className="flex w-full gap-3"
          data-testid={`ask-puntt-skill-cards-row-${rowIndex}`}
          data-row-size={rowCards.length}
        >
          {rowCards.map(renderCard)}
        </div>
      ))}
    </div>
  );
}
